import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * Themed multi-select dropdown matching the Clients page filter styling.
 *
 * Props:
 *  - options:  [{ value, label }]
 *  - value:    array of selected values
 *  - onChange: (nextValues[]) => void
 *  - allLabel: text shown when nothing is selected (e.g. "All Agents")
 *  - isDark:   boolean theme flag
 *  - searchable: show a search box inside the panel (default false)
 *  - disabled: disable the trigger
 */
const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  allLabel = 'All',
  isDark = false,
  searchable = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // De-duplicate options by value (agent names can repeat across rows).
  const uniqueOptions = useMemo(() => {
    const seen = new Set();
    return options.filter(o => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return uniqueOptions;
    const q = search.toLowerCase();
    return uniqueOptions.filter(o => String(o.label).toLowerCase().includes(q));
  }, [uniqueOptions, search]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const valueSet = useMemo(() => new Set(value), [value]);

  const toggle = (val) => {
    if (valueSet.has(val)) {
      onChange(value.filter(v => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(o => valueSet.has(o.value));
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const remove = new Set(filteredOptions.map(o => o.value));
      onChange(value.filter(v => !remove.has(v)));
    } else {
      const merged = new Set(value);
      filteredOptions.forEach(o => merged.add(o.value));
      onChange([...merged]);
    }
  };

  const summary = (() => {
    if (!value.length) return allLabel;
    if (value.length === 1) {
      const found = uniqueOptions.find(o => o.value === value[0]);
      return found ? found.label : value[0];
    }
    return `${value.length} selected`;
  })();

  const triggerClass = `w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-md text-left focus:outline-none focus:ring-2 focus:ring-purple-500 ${
    isDark ? 'bg-[#1a1f3a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={triggerClass}
      >
        <span className={`truncate ${value.length ? '' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>{summary}</span>
        <span className="flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              className={`p-0.5 rounded hover:bg-purple-500/20 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              title="Clear"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-1 w-full rounded-md shadow-xl border ${
            isDark ? 'bg-[#1a1f3a] border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          {searchable && (
            <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="relative">
                <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search..."
                  className={`w-full pl-8 pr-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                    isDark ? 'bg-[#0f1729] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
          )}

          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length > 0 && (
              <label className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer select-none border-b ${
                isDark ? 'text-gray-300 hover:bg-[#2a2f4a] border-gray-700/60' : 'text-gray-700 hover:bg-gray-50 border-gray-100'
              }`}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleAllFiltered}
                  className="w-4 h-4 rounded accent-purple-500"
                />
                <span className="font-medium">{allFilteredSelected ? 'Clear all' : 'Select all'}</span>
              </label>
            )}
            {filteredOptions.map(o => (
              <label
                key={o.value}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer select-none ${
                  isDark ? 'text-gray-300 hover:bg-[#2a2f4a]' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={valueSet.has(o.value)}
                  onChange={() => toggle(o.value)}
                  className="w-4 h-4 rounded accent-purple-500"
                />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
            {filteredOptions.length === 0 && (
              <div className={`px-3 py-3 text-sm text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No options
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
